import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, CircleHelp, LogOut, MapPin, Menu, Rocket, Sparkles, TriangleAlert, X } from 'lucide-react';
import { AuthStateResponse, OkResponse, SessionStateResponse, SupportResponse, hasMessage } from '@cloudsign/shared';
import type { Capabilities, NavEntry } from '@cloudsign/shared';
import { api } from '../lib/api';
import { setAuthState } from '../lib/authState';
import { applyBranding } from '../lib/branding';
import { t } from '../lib/i18n';
import { useCaps } from '../lib/session';
import { useSite } from '../lib/site';
import { NavIcon } from './NavIcon';
import { LogoMark } from './Logo';
import { ErrorLine } from './ErrorPanel';
import { Avatar, initials } from './ui';
import { ShellBanners } from '../modules/security/Banners';

interface Section {
  key: string;
  order: number;
  entries: NavEntry[];
}

/** Menu sections in a fixed order: the area's own sections first, "My account" last. */
export function buildSections(nav: readonly NavEntry[]): Section[] {
  const map = new Map<string, Section>();
  for (const e of nav) {
    const rank = e.area === 'account' ? 1_000_000 : 0;
    const s = map.get(e.sectionKey) ?? { key: e.sectionKey, order: rank + e.order, entries: [] };
    s.order = Math.min(s.order, rank + e.order);
    s.entries.push(e);
    map.set(e.sectionKey, s);
  }
  const sections = [...map.values()].sort((a, b) => a.order - b.order);
  for (const s of sections) s.entries.sort((a, b) => a.order - b.order);
  return sections;
}

/**
 * Splits the capabilities menu into top-bar tabs, the "Manage" mega-menu and the account entry.
 * Platform entries and the main section are tabs, and so is Users; everything else goes under Manage.
 */
export function splitNav(nav: readonly NavEntry[]): { tabs: NavEntry[]; manage: NavEntry[]; account: NavEntry | undefined } {
  const own = nav.filter((e) => e.area !== 'account');
  const isTab = (e: NavEntry) => e.area === 'platform' || e.area === 'building' || e.sectionKey === 'nav.section.main' || e.key === 'core.users';
  return {
    tabs: own.filter(isTab),
    manage: own.filter((e) => !isTab(e)),
    account: nav.find((e) => e.area === 'account'),
  };
}

function describe(e: NavEntry): string | undefined {
  const key = `${e.labelKey}.desc`;
  return hasMessage(key) ? t(key) : undefined;
}

function onPath(pathname: string, path: string): boolean {
  return path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
}

/** Closes a pop-up on Escape, on a click elsewhere, or when focus leaves it. */
function useDismiss(open: boolean, close: () => void, ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close, ref]);
}

function Wordmark() {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2 rounded-md" aria-label={t('app.name')}>
      <LogoMark />
      <span className="hidden text-base font-bold tracking-tight text-slate-900 sm:inline">{t('app.name')}</span>
    </Link>
  );
}

function TenantSwitcher({ caps }: { caps: Capabilities }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = useId();
  // Shared reception (ADR 0034): buildings this account is staff of join the list as "Building: <name>".
  const session = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => api.get('/auth/session', { schema: SessionStateResponse, quiet401: true }),
    staleTime: 60_000,
    retry: false,
  });
  const buildings = session.data?.buildings ?? [];
  const choose = useMutation({
    mutationFn: (value: string) =>
      value.startsWith('b:')
        ? api.post('/auth/select-building', { body: { buildingId: value.slice(2) }, schema: AuthStateResponse })
        : api.post('/auth/select-tenant', {
            body: value === 'platform' ? { platform: true } : { tenantId: value },
            schema: AuthStateResponse,
          }),
    onSuccess: (state) => {
      setAuthState(state);
      // Everything on screen belongs to the old organisation; the guard sends us to the right home.
      qc.removeQueries();
      void navigate('/', { replace: true });
    },
  });
  const options = [
    ...(caps.isPlatformAdmin ? [{ value: 'platform', label: t('auth.platformArea') }] : []),
    ...(caps.tenant ? [{ value: caps.tenant.id, label: caps.tenant.name }] : []),
    ...caps.otherTenants.map((o) => ({ value: o.tenantId, label: o.name })),
    ...buildings.map((b) => ({ value: `b:${b.buildingId}`, label: t('srb.shell.building', { name: b.name }) })),
  ];
  const current = caps.area === 'platform' ? 'platform' : caps.area === 'building' ? `b:${caps.building?.id ?? ''}` : (caps.tenant?.id ?? '');
  if (options.length < 2) {
    const label = caps.area === 'building' ? t('srb.shell.building', { name: caps.building?.name ?? '' }) : caps.tenant ? caps.tenant.name : t('auth.platformArea');
    return (
      <span className="inline-flex max-w-[14rem] items-center gap-2 text-sm text-slate-600">
        <Building2 aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="truncate">{label}</span>
      </span>
    );
  }
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        {t('shell.switchOrganisation')}
      </label>
      <Building2 aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <select
        id={id}
        className="min-h-9 w-full max-w-[14rem] rounded-md border border-slate-300 bg-white py-1 pl-8 pr-2 text-sm text-slate-800 hover:border-slate-400"
        value={current}
        onChange={(e) => choose.mutate(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Which site the person is working on ("here's the latest at [site]"). Hidden when there are no sites. */
export function SitePicker() {
  const { sites, siteId, site, setSiteId } = useSite();
  const id = useId();
  if (sites.length === 0 || !site) return null;
  if (sites.length === 1) {
    return (
      <span className="inline-flex max-w-[12rem] items-center gap-2 text-sm text-slate-600">
        <MapPin aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="sr-only">{t('site.picker')}: </span>
        <span className="truncate">{site.name}</span>
      </span>
    );
  }
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        {t('site.switch')}
      </label>
      <MapPin aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <select
        id={id}
        className="min-h-9 w-full max-w-[12rem] rounded-md border border-slate-300 bg-white py-1 pl-8 pr-2 text-sm text-slate-800 hover:border-slate-400"
        value={siteId ?? ''}
        onChange={(e) => setSiteId(e.target.value)}
      >
        {sites.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    </div>
  );
}

const TAB_BASE = 'relative inline-flex h-14 items-center border-b-2 px-3 text-sm font-medium transition-colors';
const TAB_ON = 'border-brand text-slate-900';
const TAB_OFF = 'border-transparent text-slate-600 hover:text-slate-900';

function MegaMenu({ entries, active }: { entries: NavEntry[]; active: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = () => setOpen(false);
  useDismiss(open, close, ref);
  const sections = buildSections(entries);
  if (entries.length === 0) return null;
  return (
    <div
      ref={ref}
      className="relative"
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget)) close();
      }}
    >
      <button
        type="button"
        className={`${TAB_BASE} gap-1 ${active || open ? TAB_ON : TAB_OFF}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        {t('shell.manage')}
        <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div
          id={panelId}
          className="absolute left-0 top-full z-40 mt-1 w-[34rem] max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          {sections.map((s, i) => (
            <div key={s.key} className={i > 0 ? 'mt-2 border-t border-slate-100 pt-2' : ''}>
              {sections.length > 1 ? <h2 className="px-3 pb-1 pt-1 text-xs font-semibold text-slate-600">{t(s.key)}</h2> : null}
              <ul className="grid gap-1 sm:grid-cols-2">
                {s.entries.map((e) => (
                  <li key={e.key}>
                    <NavLink
                      to={e.path}
                      onClick={close}
                      className={({ isActive }) => `flex items-start gap-3 rounded-lg p-3 hover:bg-slate-50 ${isActive ? 'bg-slate-50' : ''}`}
                    >
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                        <NavIcon name={e.icon} className="h-5 w-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">{t(e.labelKey)}</span>
                        {describe(e) ? <span className="block text-xs text-slate-600">{describe(e)}</span> : null}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const MENU_ITEM = 'flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-800 hover:bg-slate-50';

/** Account-menu "Support": a mailto link to the platform's support address (core.support.email). Hidden when that is empty. */
function SupportLink({ onClick }: { onClick: () => void }) {
  const q = useQuery({
    queryKey: ['support'],
    queryFn: () => api.get('/support', { schema: SupportResponse }),
    staleTime: 5 * 60_000,
  });
  const email = q.data?.email;
  if (!email) return null;
  return (
    <a href={`mailto:${email}?subject=${encodeURIComponent(t('shell.support.subject'))}`} onClick={onClick} className={MENU_ITEM}>
      <CircleHelp aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
      {t('shell.support')}
    </a>
  );
}

function ProfileMenu({ caps, account, onSignOut, busy }: { caps: Capabilities; account: NavEntry | undefined; onSignOut: () => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = () => setOpen(false);
  useDismiss(open, close, ref);
  return (
    <div
      ref={ref}
      className="relative"
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget)) close();
      }}
    >
      <button
        type="button"
        aria-label={t('shell.profileMenu')}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand text-xs font-semibold text-on-brand hover:bg-brand-hover"
      >
        <span aria-hidden="true">{initials(caps.account.fullName)}</span>
      </button>
      {open ? (
        <div id={panelId} className="absolute right-0 top-full z-40 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar name={caps.account.fullName} className="h-9 w-9 text-xs" />
            <div className="min-w-0">
              <p className="text-xs text-slate-600">{t('shell.signedInAs')}</p>
              <p className="truncate text-sm font-semibold text-slate-900">{caps.account.fullName}</p>
              <p className="truncate text-xs text-slate-600">{caps.account.email}</p>
            </div>
          </div>
          <div className="my-1 border-t border-slate-100" />
          {account ? (
            <NavLink to={account.path} onClick={close} className="flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-800 hover:bg-slate-50">
              <NavIcon name={account.icon} className="h-4 w-4 shrink-0 text-slate-500" />
              {t(account.labelKey)}
            </NavLink>
          ) : null}
          {caps.area === 'tenant' ? (
            <NavLink to="/getting-started" onClick={close} className={MENU_ITEM}>
              <Rocket aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
              {t('shell.gettingStarted')}
            </NavLink>
          ) : null}
          <NavLink to="/whats-new" onClick={close} className={MENU_ITEM}>
            <Sparkles aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
            {t('shell.whatsNew')}
          </NavLink>
          <SupportLink onClick={close} />
          <button
            type="button"
            disabled={busy}
            onClick={onSignOut}
            className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            <LogOut aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-500" />
            {t('auth.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Drawer({ caps, onClose, onSignOut, busy }: { caps: Capabilities; onClose: () => void; onSignOut: () => void; busy: boolean }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const sections = buildSections(caps.nav);
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={onClose} />
      <div
        id="site-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.drawer')}
        className="absolute inset-y-0 left-0 flex w-80 max-w-[85vw] flex-col overflow-y-auto bg-white shadow-xl"
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4">
          <span className="flex items-center gap-2">
            <LogoMark />
            <span className="text-base font-bold tracking-tight">{t('app.name')}</span>
          </span>
          <button
            ref={closeRef}
            type="button"
            aria-label={t('shell.closeMenu')}
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100"
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="space-y-3 border-b border-slate-100 p-4">
          <TenantSwitcher caps={caps} />
          <SitePicker />
        </div>
        <nav aria-label={t('shell.mainMenu')} className="flex-1 p-3">
          {sections.map((s) => (
            <div key={s.key} className="mb-4">
              <h2 className="px-3 pb-1 text-xs font-semibold text-slate-600">{t(s.key)}</h2>
              <ul>
                {s.entries.map((e) => (
                  <li key={e.key}>
                    <NavLink
                      to={e.path}
                      end={e.path === '/'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
                          isActive ? 'bg-brand-soft text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                        }`
                      }
                    >
                      <NavIcon name={e.icon} />
                      {t(e.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <button
            type="button"
            disabled={busy}
            onClick={onSignOut}
            className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <LogOut aria-hidden="true" className="h-5 w-5 shrink-0" />
            {t('auth.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The light left menu on settings-style pages ("Manage settings"). */
function SubMenu({ entries }: { entries: NavEntry[] }) {
  const sections = buildSections(entries);
  return (
    <nav aria-label={t('shell.settingsMenu')} className="hidden w-56 shrink-0 lg:block">
      <h2 className="mb-3 px-3 text-sm font-semibold text-slate-900">{t('shell.settingsMenu')}</h2>
      {sections.map((s, i) => (
        <div key={s.key} className={i > 0 ? 'mt-3 border-t border-slate-200 pt-3' : ''}>
          {sections.length > 1 ? <h3 className="px-3 pb-1 text-xs font-semibold text-slate-600">{t(s.key)}</h3> : null}
          <ul>
            {s.entries.map((e) => (
              <li key={e.key}>
                <NavLink
                  to={e.path}
                  className={({ isActive }) =>
                    `flex min-h-9 items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm ${
                      isActive ? 'bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-700 hover:bg-slate-100'
                    }`
                  }
                >
                  <NavIcon name={e.icon} className="h-4 w-4 shrink-0 text-slate-500" />
                  {t(e.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function Shell({ children }: { children?: ReactNode }) {
  const caps = useCaps();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout', { schema: OkResponse }),
    onSettled: () => {
      setAuthState(null);
      qc.clear();
      applyBranding(null);
      void navigate('/login', { replace: true });
    },
  });
  useEffect(() => setDrawer(false), [pathname]);

  const { tabs, manage, account } = splitNav(caps.nav);
  const activeManage = manage.some((e) => onPath(pathname, e.path));
  const evacuation =
    caps.area === 'building'
      ? caps.nav.find((e) => e.key === 'shared-reception.building.evacuation')
      : caps.permissions.includes('evacuation.view')
        ? caps.nav.find((e) => e.key.startsWith('evacuation'))
        : undefined;
  const showSubMenu = activeManage && caps.area === 'tenant';

  const main = (
    <main id="main" tabIndex={-1} className="min-w-0 flex-1">
      <ErrorLine error={signOut.error} />
      {children ?? <Outlet />}
    </main>
  );

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:bg-white focus:p-2">
        {t('shell.skipToContent')}
      </a>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-4 sm:px-6">
          <button
            type="button"
            className="-ml-2 inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 lg:hidden"
            aria-expanded={drawer}
            aria-controls="site-drawer"
            aria-label={t('shell.openMenu')}
            onClick={() => setDrawer(true)}
          >
            <Menu aria-hidden="true" />
          </button>
          <Wordmark />
          <nav aria-label={t('shell.mainMenu')} className="ml-4 hidden items-center lg:flex">
            {tabs.map((e) => (
              <NavLink key={e.key} to={e.path} end={e.path === '/'} className={({ isActive }) => `${TAB_BASE} ${isActive ? TAB_ON : TAB_OFF}`}>
                {t(e.labelKey)}
              </NavLink>
            ))}
            <MegaMenu entries={manage} active={activeManage} />
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {evacuation ? (
              <Link
                to={evacuation.path}
                className="inline-flex min-h-9 items-center gap-2 rounded-md border border-red-600 px-3 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <TriangleAlert aria-hidden="true" className="h-4 w-4" />
                <span className="hidden sm:inline">{t('shell.evacuate')}</span>
                <span className="sr-only sm:hidden">{t('shell.evacuate')}</span>
              </Link>
            ) : null}
            <div className="hidden items-center gap-3 lg:flex">
              <SitePicker />
              <TenantSwitcher caps={caps} />
            </div>
            <ProfileMenu caps={caps} account={account} busy={signOut.isPending} onSignOut={() => signOut.mutate()} />
          </div>
        </div>
      </header>
      <ShellBanners />
      {drawer ? <Drawer caps={caps} onClose={() => setDrawer(false)} busy={signOut.isPending} onSignOut={() => signOut.mutate()} /> : null}
      <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        {showSubMenu ? (
          <div className="flex gap-8">
            <SubMenu entries={manage} />
            {main}
          </div>
        ) : (
          main
        )}
      </div>
    </div>
  );
}
